// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Dictionary } from 'typescript-collections';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { Line } from '@3d-scene/model/line';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Vector3 } from 'three';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ProjectModel } from '@project/model/project.model';
import { MoveArticlesBetweenBlocksData } from '@shared/undo/data/move-articles-between-blocks-data';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleMoveManager, ArticlesMovedHandlerData } from '@project/components/article/manager/article-move.manager';
import { NumberUtils } from '@shared/util/number-utils';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { UnusedLabelingService } from '../../label/label-editor/service/unused-labeling.service';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { Injectable } from '@angular/core';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { SelectionModelService } from '../service/selection-model.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { MessageEntry } from '@shared/alert/message-entry';

export class MovedArticlesInBlock {
  public blockGuid: string;
  public bmkName: string;
  public articleGuids: string[] = [];
}

@Injectable({ providedIn: 'root' })
export class MoveArticlePresenter {
  public initialized: boolean = false;

  public rangeOnLineArticleType: string = null;

  public lastArticleBeforeSelectionExists: boolean = false;

  private _parentUsageIdToSelectedArticles: Dictionary<string, ArticleContainer[]> = new Dictionary<string, ArticleContainer[]>();
  private _insertHelpers: InsertArticleHelper[] = [];
  private _errorMessage: MessageEntry = null;
  private _moveArticlesMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
  private _articleUsageIds: string[] = [];

  private _moveArticlesBetweenBlocksDatas: MoveArticlesBetweenBlocksData[];
  private _articlePositionToArticleGUID: Dictionary<number, string> = new Dictionary<number, string>();
  private _allowedPositions: number[] = [];
  private _maxAllowedArticlePosition: number = NaN;

  constructor(
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private selectionModelService: SelectionModelService,
    private carrierBlockManager: CarrierBlockManager,
    private articleMoveManager: ArticleMoveManager,
    private sceneManager: SceneManager,
    private layerManager: LayerManager,
    private projectContentPresenter: ProjectContentPresenter,
    private unusedLabelingService: UnusedLabelingService,
    private insertArticleUtils: InsertArticleUtils,
    private articleContainerUtils: ArticleContainerUtils,
    private containerService: ContainerService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private articleConnectionService: ArticleConnectionService
  ) {
    const selectedArticleContainers: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    this.initWithArticleContainers(selectedArticleContainers);
  }

  public initWithArticleContainers(articleContainers: ArticleContainer[]): void {
    this.initialized = false;
    this.rangeOnLineArticleType = null;
    this.lastArticleBeforeSelectionExists = false;
    this._parentUsageIdToSelectedArticles = new Dictionary<string, ArticleContainer[]>();
    this._insertHelpers = [];
    this._articlePositionToArticleGUID = new Dictionary<number, string>();
    this._allowedPositions = [];
    this._maxAllowedArticlePosition = NaN;
    this._errorMessage = null;
    this._moveArticlesMap = new Dictionary<string, Vector3>();
    this._moveArticlesBetweenBlocksDatas = [];
    this._articleUsageIds = [];
    this.storeArticlesOnRangeOnLineArticle(articleContainers);
    if (this._insertHelpers.length > 0) {
      this.lastArticleBeforeSelectionExists = this.isLastArticleBeforeSelectionExists();
      this.initialized = true;
    } else {
      this.initialized = false;
    }
  }

  private clear(): void {
    this._errorMessage = null;
    this._moveArticlesMap = null;
  }

  public async moveArticlesByRangeOnLineArticleStart(offset: number): Promise<void> {
    if (!this.initialized) {
      return;
    }
    this.clear();
    let articlesMoved: boolean = false;
    for (const insertHelper of this._insertHelpers) {
      articlesMoved = this.moveArticleGroupFromStart(offset, insertHelper) || articlesMoved;
    }
    await this.completeMoveArticlesOperation(articlesMoved);
  }

  private moveArticleGroupFromStart(offset: number, insertHelper: InsertArticleHelper): boolean {
    const selectedContainers: ArticleContainer[] = this.getSelectedArticlesByAcceptingUsageId(insertHelper.acceptingArticleUsageId);
    if (ArrayUtils.isEmpty(selectedContainers)) {
      return false;
    }

    const startPoint: Vector3 = insertHelper.baseLine.start;
    return this.moveArticles(selectedContainers, startPoint, offset, insertHelper);
  }

  public async moveArticlesByNeighbor(offset: number): Promise<void> {
    this.clear();
    let articlesMoved: boolean = false;
    for (const insertHelper of this._insertHelpers) {
      articlesMoved = this.moveArticleGroupByNeighbor(offset, insertHelper) || articlesMoved;
    }
    await this.completeMoveArticlesOperation(articlesMoved);
  }

  private moveArticleGroupByNeighbor(offset: number, insertHelper: InsertArticleHelper): boolean {
    const selectedContainers: ArticleContainer[] = this.getSelectedArticlesByAcceptingUsageId(insertHelper.acceptingArticleUsageId);
    if (ArrayUtils.isEmpty(selectedContainers)) {
      return false;
    }
    const lastArticleBeforeSelection: ArticleContainer = this.findNeighborArticle(selectedContainers, insertHelper);
    if (!lastArticleBeforeSelection) {
      return false;
    }

    const neighborLine: AdvancedLine = insertHelper.getArticleLine(lastArticleBeforeSelection);
    const commonArticleLine: AdvancedLine = insertHelper.calculateCommonArticlesLine(selectedContainers);
    const maxOverlapLenght: number = AdvancedLine.calculateMaxOverlapLength(commonArticleLine, neighborLine);

    return this.moveArticles(selectedContainers, neighborLine.end, offset - maxOverlapLenght, insertHelper);
  }

  private async completeMoveArticlesOperation(articlesMoved: boolean): Promise<void> {
    if (this._errorMessage) {
      this.showErrorMessage(this._errorMessage);
    }
    if (articlesMoved) {
      await this.pushUndoMoveOperation(this._moveArticlesMap, true, false, this._articleUsageIds);
      this.sceneManager.renderScene();
      const data: ArticlesMovedHandlerData = {
        articleOrderChanged: true,
        nonMovableArticlesMoved: false,
        articleUsageIds: this._articleUsageIds,
      };
      this.layerManager.queueUpdate();
      this.projectModel.projectChangeHandler();
      this.unusedLabelingService.articleContainerMovedHandler(data);
      this.articleMoveManager.articleContainerMovedHandler(data);
      this.projectContentPresenter.articleContainerMovedHandler(data);
    }
  }

  private isLastArticleBeforeSelectionExists(): boolean {
    for (const insertHelper of this._insertHelpers) {
      const selectedContainers: ArticleContainer[] = this.getSelectedArticlesByAcceptingUsageId(insertHelper.acceptingArticleUsageId);
      const neighborArticle: ArticleContainer = this.findNeighborArticle(selectedContainers, insertHelper);
      if (neighborArticle) {
        return true;
      }
    }
    return false;
  }

  private getSelectedArticlesByAcceptingUsageId(usageId: string): ArticleContainer[] {
    if (!usageId) {
      return [];
    }
    return this._parentUsageIdToSelectedArticles.getValue(usageId);
  }

  /**
   * @return last article placed before given articles
   */
  private findNeighborArticle(articleContainers: ArticleContainer[], insertHelper: InsertArticleHelper): ArticleContainer {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return null;
    }
    const commonLine: Line = insertHelper.calculateCommonArticlesLine(articleContainers);
    // articles placed before given articles
    const articlesAtStart: ArticleContainer[] = insertHelper.getConnectedArticlesFromLine(
      new AdvancedLine(insertHelper.startPoint, commonLine.start),
      null,
      true
    );
    return ArrayUtils.isEmpty(articlesAtStart) ? null : articlesAtStart[articlesAtStart.length - 1];
  }

  private moveArticles(articleContainers: ArticleContainer[], startPosition: Vector3, offset: number, insertHelper: InsertArticleHelper): boolean {
    if (!this.initialized) {
      return false;
    }
    let articlesMoved: boolean = false;
    if (this.isAllCombinedArticlesExists(articleContainers)) {
      articleContainers = insertHelper.sortArticles(articleContainers);
      const commonLine: AdvancedLine = insertHelper.calculateCommonArticlesLine(articleContainers);

      const distanceFromStart: number = insertHelper.getDistanceFromStart(startPosition) + offset;
      const startOffsetPoint: Vector3 = insertHelper.calculatePointByOffset(distanceFromStart);
      const moveOffset: Vector3 = startOffsetPoint.clone().sub(commonLine.start);
      if (MathUtils.equalsNumber3D(moveOffset, new Vector3())) {
        return false; // articles position wasn't changed
      }

      const commonLineAfterMove: AdvancedLine = commonLine.move(moveOffset) as AdvancedLine;
      const moveMap: Dictionary<string, Vector3> = insertHelper.calculateMoveOffsetByArticleLine(
        commonLineAfterMove,
        null,
        ArticleContainerUtils.extractArticleGUIDs(articleContainers)
      );
      if (moveMap && this.insertArticleUtils.getBreakCombinedArticles(moveMap).length === 0) {
        for (const moveArticle of articleContainers) {
          moveMap.setValue(moveArticle.articleGUID, moveOffset);
        }
        this.insertArticleUtils.moveArticlesByMap(moveMap);
        this.mergeMoveArticlesMap(moveMap);
        this.refreshMetaElements(insertHelper.acceptingArticleUsageId);
        this._moveArticlesBetweenBlocksDatas = this.carrierBlockManager.getMoveArticlesBetweenBlocksDatas(articleContainers, insertHelper, commonLineAfterMove);
        articlesMoved = true;
      } else {
        this._errorMessage = {
          title: 'moveArticlePopup.title.error_free_space',
          message: 'moveArticlePopup.message.error_free_space',
          type: "error",
        };
      }
    } else {
      this._errorMessage = {
        title: 'moveArticlePopup.title.error_article_connected',
        message: 'moveArticlePopup.message.error_article_connected',
        type: "error",
      };
    }
    return articlesMoved;
  }

  private mergeMoveArticlesMap(moveMap: Dictionary<string, Vector3>): void {
    if (!moveMap) {
      return;
    }
    if (!this._moveArticlesMap) {
      this._moveArticlesMap = new Dictionary<string, Vector3>();
    }
    InsertArticleUtils.mergeMoveMap(this._moveArticlesMap, moveMap);
  }

  private showErrorMessage(errorMessage: {title: string, message: string}): void {
    if (!errorMessage) {
      return;
    }
    this.alertHelper.showError(errorMessage.title, errorMessage.message);
  }

  private isAllCombinedArticlesExists(baseArticles: ArticleContainer[]): boolean {
    const processedArticleIds: string[] = [];
    for (const baseArticle of baseArticles) {
      if (processedArticleIds.indexOf(baseArticle.articleGUID) > -1) {
        continue;
      }
      const combinedArticles: ArticleContainer[] = this.articleContainerUtils.findCombinedArticlesWithEndplates(baseArticle, true, true);
      for (const combinedArticle of combinedArticles) {
        processedArticleIds.push(combinedArticle.articleGUID);
        if (!this.articleContainerUtils.findArticleContainerByGUID(combinedArticle.articleGUID, baseArticles)) {
          return false;
        }
      }
    }
    return true;
  }

  private async pushUndoMoveOperation(moveMap: Dictionary<string, Vector3>, rebuildProjectTree: boolean, nonMovableArticlesMoved: boolean,
    articleUsageIds: string[]): Promise<void> {
    await this.articleMoveManager.createAndStoreMoveOperation(moveMap, rebuildProjectTree, nonMovableArticlesMoved, articleUsageIds, true,
      this._moveArticlesBetweenBlocksDatas);
  }

  private refreshMetaElements(articleUsageId: string): void {
    if (!articleUsageId) {
      return;
    }
    const parentArticle: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
    if (parentArticle) {
      parentArticle.refreshMetaElements();
    }
  }

  private storeArticlesOnRangeOnLineArticle(articleContainers: ArticleContainer[]): void {
    for (const articleContainer of articleContainers) {
      if (!this.articleConnectionService.isArticleConnectedToRangeOnLineArticleWithConfigured(articleContainer)) {
        continue;
      }
      const parentUsageId: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(articleContainer.articleUsage);
      if (!this._parentUsageIdToSelectedArticles.containsKey(parentUsageId)) {
        this._parentUsageIdToSelectedArticles.setValue(parentUsageId, []);
        this.storeInsertHelper(parentUsageId);
      }
      const groupedSelectedArticles: ArticleContainer[] = this._parentUsageIdToSelectedArticles.getValue(parentUsageId);
      groupedSelectedArticles.push(articleContainer);
      if (!this.rangeOnLineArticleType) {
        this.rangeOnLineArticleType = this.articleContainerUtils.getArticleTypeByArticleUsageId(parentUsageId);
      }
      this._articleUsageIds.push(articleContainer.articleGUID);
    }
  }

  private storeInsertHelper(parentArticleUsageId: string): void {
    if (!parentArticleUsageId) {
      return;
    }
    const parentContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentArticleUsageId);
    const insertHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(parentContainer);
    if (insertHelper) {
      this._insertHelpers.push(insertHelper);
    }
  }

  public getAllowedMovePositions(): number[] {
    const selectedArticles: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    if (this._insertHelpers.length !== 1 || !this.articleContainerUtils.isAllArticlesConnectedToSameRangeOnLineContainer(selectedArticles)) {
      return [];
    }
    if (ArrayUtils.isNotEmpty(this._allowedPositions)) {
      return this._allowedPositions;
    }
    this._allowedPositions = [];
    const insertArticleHelper: InsertArticleHelper = this._insertHelpers[0];
    const connectedArticles: ArticleContainer[] = insertArticleHelper.getAllConnectedArticles();

    for (let i = 0; i < connectedArticles.length; i++) {
      const article: ArticleContainer = connectedArticles[i];
      const position: number = i + 1;
      if (!this.isArticleInArray(article.articleGUID, selectedArticles)) {
        this._allowedPositions.push(position);
      }
      this._articlePositionToArticleGUID.setValue(position, article.articleGUID);
    }
    return this._allowedPositions;
  }

  public getAllowedMaxPosition(): number {
    const selectedArticles: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    if (this._insertHelpers.length !== 1 || !this.articleContainerUtils.isAllArticlesConnectedToSameRangeOnLineContainer(selectedArticles)) {
      return Math.pow(2, 31) - 1;
    }
    if (NumberUtils.isNumber(this._maxAllowedArticlePosition)) {
      return this._maxAllowedArticlePosition;
    }
    const insertArticleHelper: InsertArticleHelper = this._insertHelpers[0];
    const connectedArticles: ArticleContainer[] = insertArticleHelper.getAllConnectedArticles();
    this._maxAllowedArticlePosition = connectedArticles.length;
    return this._maxAllowedArticlePosition;
  }

  private isArticleInArray(articleGUID: String, selectedArticles: ArticleContainer[]): boolean {
    for (const selectedArticle of selectedArticles) {
      if (selectedArticle.articleGUID === articleGUID) {
        return true;
      }
    }
    return false;
  }

  public async moveArticlesByPosition(position: number): Promise<void> {
    if (NumberUtils.isNotNumber(position) || !this._articlePositionToArticleGUID.containsKey(position)) {
      return;
    }

    const insertHelper: InsertArticleHelper = this._insertHelpers[0];
    const articleGUID: string = this._articlePositionToArticleGUID.getValue(position);
    const articleLine: AdvancedLine = insertHelper.getArticleLineByGuid(articleGUID);

    const selectedContainers: ArticleContainer[] = this.getSelectedArticlesByAcceptingUsageId(insertHelper.acceptingArticleUsageId);
    const sortedArticles: ArticleContainer[] = insertHelper.sortArticles(selectedContainers);
    const commonMoveLine: AdvancedLine = insertHelper.calculateCommonArticlesLine(sortedArticles);
    const isMoveForward: boolean = insertHelper.getDistanceFromStart(commonMoveLine.start) < insertHelper.getDistanceFromStart(articleLine.start);

    let nextLine: AdvancedLine = null;
    const nextPosition: number = isMoveForward ? position + 1 : position - 1;
    if (this._articlePositionToArticleGUID.containsKey(nextPosition)) {
      nextLine = insertHelper.getArticleLineByGuid(this._articlePositionToArticleGUID.getValue(nextPosition));
    }

    let offset: number;
    if (
      nextLine &&
      articleLine.hasLine(nextLine, false) &&
      !MathUtils.equalsNumber(articleLine.end.distanceTo(nextLine.start), 0) &&
      !MathUtils.equalsNumber(articleLine.start.distanceTo(nextLine.end), 0)
    ) {
      if (isMoveForward) {
        const overlapOffset: number = Math.min(
          articleLine.end.distanceTo(nextLine.start),
          AdvancedLine.calculateMaxOverlapLengthInInnerLine(nextLine, commonMoveLine)
        );
        offset = insertHelper.getDistanceFromStart(articleLine.end) - overlapOffset;
      } else {
        const overlapOffset: number = Math.min(
          articleLine.start.distanceTo(nextLine.end),
          AdvancedLine.calculateMaxOverlapLengthInInnerLine(nextLine, commonMoveLine, false)
        );
        offset = insertHelper.getDistanceFromStart(nextLine.end) - overlapOffset;
      }
    } else {
      if (isMoveForward) {
        offset = insertHelper.getDistanceFromStart(articleLine.end) - AdvancedLine.calculateMaxOverlapLength(articleLine, commonMoveLine);
      } else {
        offset = insertHelper.getDistanceFromStart(articleLine.start);
      }
    }

    await this.moveArticlesByRangeOnLineArticleStart(offset);
  }
}
